import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, usePaginationFragment } from 'react-relay';

import { AdminTablePlaceholderRows } from 'src/components/Admin/AdminTablePlaceholder';
import BadgeActiveTimeRange from 'src/components/Badge/BadgeActiveTimeRange';
import PlaceholderErrorModal from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/PlaceholderErrorModal';
import LoadMoreButton from 'src/components/LoadMoreButton';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { DEFAULT_LIST_PAGE_SIZE } from 'src/util/constants';
import logger from 'src/util/logger';

const messages = defineMessages({
  paginationError: {
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table.pagination_error.message',
    defaultMessage: 'Failed to load more primary billing points. Please try again.',
  },
});

const AdminPrimaryBillingPointListFragment = graphql`
  fragment AdminPrimaryBillingPointList_viewer on Viewer
  @throwOnFieldError
  @refetchable(queryName: "AdminPrimaryBillingPointListRefetchQuery")
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int" }
  ) {
    id
    viewerUser {
      email
      givenName
      familyName
    }
    primaryBillingPoints(
      first: $count
      after: $cursor
    ) @connection(key: "AdminPrimaryBillingPointList_primaryBillingPoints") {
      edges {
        cursor
        node {
          uuid
          externalIdentifier
          identifier
          active {
            start
            finish
          }
          property {
            uuid
            externalIdentifier
            title
            active {
              start
              finish
            }
          }
        }
      }
    }
  }
`;

/**
 * Skeleton rows rendered behind the pagination error modal, inside the loaded
 * table, to stand in for the page that failed to load.
 * @returns {React.ReactElement} Skeleton table rows.
 */
function PaginationErrorRows() {
  return <AdminTablePlaceholderRows columnCount={7} rowCount={3} />;
}

/**
 * Admin list of primary billing points with load-more pagination. Field errors
 * throw to the route's PlaceholderErrorBoundary; load-more failures show an
 * inline retry modal.
 * @param {object} props - Component props.
 * @param {object|null} props.viewer - Relay fragment reference for the viewer.
 * @returns {React.ReactComponentElement} - AdminPrimaryBillingPointList component
 */
function AdminPrimaryBillingPointList({ viewer: viewerRef = null }) {
  const {
    data, loadNext, hasNext, isLoadingNext,
  } = usePaginationFragment(AdminPrimaryBillingPointListFragment, viewerRef);
  const [paginationError, setPaginationError] = useState(null);
  const intl = useIntl();

  /**
   * Loads the next page of primary billing points, keeping a failure for the retry modal.
   */
  const loadMore = () => {
    if (!hasNext || isLoadingNext) {
      return;
    }

    setPaginationError(null);
    loadNext(DEFAULT_LIST_PAGE_SIZE, {
      onComplete: (error) => {
        if (error) {
          logger.error({ error });
          setPaginationError(error);
        }
      },
    });
  };

  if (!data) {
    // A completed query without a viewer would otherwise never leave the spinner.
    throw new Error('AdminPrimaryBillingPointList: the route query returned no viewer');
  }

  const { primaryBillingPoints } = data;

  // Breadcrumb names
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.breadcrumb.admin',
    defaultMessage: 'Admin',
  });
  const breadcrumbPrimaryBillingPoints = intl.formatMessage({
    id: 'admin.breadcrumb.primary_billing_points',
    defaultMessage: 'Primary Billing Points',
  });

  // Page title and headings
  const pageTitle = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.page_title',
    defaultMessage: 'Enosi Admin - Primary Billing Points',
  });
  const pageHeading = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.page_heading',
    defaultMessage: 'Primary Billing Points',
  });

  // Table headers - Primary Billing Point section
  const headerPrimaryBillingPoint = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.primary_billing_point',
    defaultMessage: 'Primary Billing Point',
  });
  const headerActive = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.active',
    defaultMessage: 'Active',
  });
  const headerIdentifier = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.identifier',
    defaultMessage: 'Identifier',
  });
  const headerId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.id',
    defaultMessage: 'ID',
  });
  const headerExtId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.external_identifier',
    defaultMessage: 'Ext ID',
  });
  const tooltipExtId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.tooltip.ext_id',
    defaultMessage: 'External identifier',
  });

  // Table headers - Property section
  const headerProperty = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.property',
    defaultMessage: 'Property',
  });
  const headerTitle = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.table_header.title',
    defaultMessage: 'Title',
  });

  // Load more button text
  const copyLoadMore = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.load_more_button.state_load_more.label',
    defaultMessage: 'Load more...',
  });
  const copyNoMore = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_list.load_more_button.state_no_more.label',
    defaultMessage: 'No more primary billing points to load',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPrimaryBillingPoints },
        ]}
      />

      <h1>{pageHeading}</h1>

      <div className="mt-4 mb-4 card">
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th scope="col" colSpan={4} className="text-center">{headerPrimaryBillingPoint}</th>
                <th scope="col" colSpan={3} className="text-center">{headerProperty}</th>
              </tr>
              <tr>
                <th scope="col">{headerActive}</th>
                <th scope="col">{headerIdentifier}</th>
                <th scope="col">{headerId}</th>
                <th scope="col">
                  <abbr title={tooltipExtId}>{headerExtId}</abbr>
                </th>
                <th scope="col">{headerTitle}</th>
                <th scope="col">
                  <abbr title={tooltipExtId}>{headerExtId}</abbr>
                </th>
                <th scope="col">{headerActive}</th>
              </tr>
            </thead>
            <tbody>
              {
                primaryBillingPoints
                && primaryBillingPoints.edges
                && primaryBillingPoints.edges.map((edge) => {
                  const { node } = edge;
                  const {
                    uuid, externalIdentifier, identifier, property, active,
                  } = node;

                  return (
                    <tr key={`admin-primaryBillingPoint-${uuid}`}>
                      <td>
                        <BadgeActiveTimeRange active={active} />
                      </td>
                      <th scope="row" colSpan={3}>
                        <Link to={`/admin/primary-billing-points/${uuid}`}>
                          {identifier}
                        </Link>
                        <br />
                        <UUID uuid={uuid} />
                        <br />
                        {externalIdentifier}
                      </th>
                      {property && (
                        <>
                          <td colSpan={2}>
                            <Link to={`/admin/properties/${property.uuid}`}>
                              {property.title}
                            </Link>
                            <br />
                            {property.externalIdentifier}
                            <br />
                            <UUID uuid={property.uuid} />
                          </td>
                          <td>
                            <BadgeActiveTimeRange active={property.active} />
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })
              }
              {/* The modal renders through a portal; only its skeleton rows render here. */}
              {paginationError && (
                <PlaceholderErrorModal
                  bodyText={intl.formatMessage(messages.paginationError)}
                  error={paginationError}
                  onCancelFunc={() => setPaginationError(null)}
                  onRetryFunc={loadMore}
                  placeholderComponent={PaginationErrorRows}
                  retryDisabled={isLoadingNext}
                />
              )}
            </tbody>
          </table>
        </div>
        <div className="card-footer">
          <LoadMoreButton
            copyLoadMore={copyLoadMore}
            copyNoMore={copyNoMore}
            onClick={loadMore}
            hasNext={hasNext}
          />
        </div>
      </div>
    </>
  );
}

AdminPrimaryBillingPointList.propTypes = {
  viewer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default AdminPrimaryBillingPointList;
